import { db } from "../config";
import {
  collection,
  doc,
  setDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  increment,
} from "firebase/firestore";

export class ChatService {
  private conversationsRef = collection(db, "chat_conversations");

  /**
   * Gera um ID único e consistente para o par de usuários
   */
  private getChatId(uid1: string, uid2: string): string {
    return uid1 < uid2 ? `${uid1}_${uid2}` : `${uid2}_${uid1}`;
  }

  /**
   * Cria ou ativa uma conversa entre dois usuários (o "Match/Liberação")
   */
  async createOrGetConversation(
    myUid: string,
    myData: any,
    targetUid: string,
    targetData: any,
    isSubscriber: boolean,
  ) {
    if (!isSubscriber) {
      throw new Error("USER_NOT_SUBSCRIBER");
    }

    const chatId = this.getChatId(myUid, targetUid);
    const chatDocRef = doc(this.conversationsRef, chatId);

    // Sempre cria ou retorna o chat entre o usuário logado e o alvo clicado
    const { getDoc } = await import("firebase/firestore");
    const chatSnap = await getDoc(chatDocRef);

    if (!chatSnap.exists()) {
      const initialData = {
        id: chatId,
        users: [myUid, targetUid],
        status: "active", //"pending",
        lastUpdate: serverTimestamp(),
        lastMessage: "Solicitação de conversa enviada",
        unreadCount: {
          [myUid]: 0,
          [targetUid]: 0,
        },
        participantData: {
          [myUid]: {
            username: myData.username,
            profileImage: myData.profileImage || null,
          },
          [targetUid]: {
            username: targetData.username,
            profileImage: targetData.profileImage || null,
          },
        },
        requestedBy: myUid,
      };

      await setDoc(chatDocRef, initialData);

      // // 🔔 dispara notificação push
      // try {
      //   const { getFunctions, httpsCallable } = await import(
      //     "firebase/functions"
      //   );
      //   const functions = getFunctions();
      //   const sendNotification = httpsCallable(functions, "sendNotification");

      //   await sendNotification({
      //     targetUid,
      //     messageText: "Existem novas pessoas querendo conversar com você!",
      //   });

      //   console.log("Push enviada com sucesso");
      // } catch (error) {
      //   console.error("Erro enviando push:", error);
      // }
    }

    return chatId;
  }

  async acceptChatRequest(chatId: string) {
    const chatRef = doc(db, "chat_conversations", chatId);
    await updateDoc(chatRef, {
      status: "active",
      lastMessage: "Solicitação aceita! Vocês já podem conversar.",
      lastUpdate: serverTimestamp(),
    });
  }

  /**
   * Envia uma mensagem e atualiza o "ponta" da conversa
   */
  async sendMessage(
    chatId: string,
    senderId: string,
    receiverId: string,
    text: string,
  ) {
    const messagesRef = collection(
      db,
      "chat_conversations",
      chatId,
      "messages",
    );
    const chatDocRef = doc(this.conversationsRef, chatId);

    // 1. Adiciona a mensagem na subcoleção
    await addDoc(messagesRef, {
      text,
      senderId,
      createdAt: serverTimestamp(),
      read: false,
    });

    // 2. Atualiza o documento pai (Denormalização para performance)
    await updateDoc(chatDocRef, {
      lastMessage: text,
      lastUpdate: serverTimestamp(),
      [`unreadCount.${receiverId}`]: increment(1),
    });
  }

  /**
   * Limpa o contador de não lidas quando o usuário abre o chat
   */
  async markAsRead(chatId: string, myUid: string) {
    const chatDocRef = doc(this.conversationsRef, chatId);

    await updateDoc(chatDocRef, {
      [`unreadCount.${myUid}`]: 0,
    });

    const { getDocs, query, collection, where, writeBatch } = await import(
      "firebase/firestore"
    );
    const messagesRef = collection(
      db,
      "chat_conversations",
      chatId,
      "messages",
    );

    const unreadMessagesQuery = query(
      messagesRef,
      where("senderId", "!=", myUid),
      where("read", "==", false),
    );

    const snapshot = await getDocs(unreadMessagesQuery);

    if (!snapshot.empty) {
      const batch = writeBatch(db);
      snapshot.docs.forEach((msgDoc) => {
        batch.update(msgDoc.ref, { read: true });
      });
      await batch.commit();
    }
  }

  /**
   * Retorna a Query para as mensagens (usado com useCollectionData)
   */
  getMessagesQuery(chatId: string) {
    return query(
      collection(db, "chat_conversations", chatId, "messages"),
      orderBy("createdAt", "asc"),
    );
  }

  /**
   * Retorna a Query para as conversas do usuário (usado com useCollectionData)
   */
  getMyConversationsQuery(myUid: string) {
    return query(
      this.conversationsRef,
      where("users", "array-contains", myUid),
      // orderBy("lastUpdate", "desc"),
    );
  }
}

export const chatService = new ChatService();
